import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import CategoryIcon from './CategoryIcon';
import { Category } from '../types/foundObject';
import type { ProviderStorageBoxRow } from '../types/storageBox';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { comparePlacesByLabel } from '../utils/comparePlaceLabels';
import CategoryAttributeFilterControls from './CategoryAttributeFilterControls';
import ReferenceSuffixFilterField from './ReferenceSuffixFilterField';
import { pruneDependentAttributeFilters } from '../utils/categoryAttributeFilters';
import { formatAttributeLabel } from '../utils/attributeLabels';
import { formatReferenceFilterLabel } from '../utils/foundObjectReference';

export interface FilterState {
  search: string;
  /** Numéro d’étiquette après le tiret (ex. « 2 » pour T4526-2). */
  referenceCode: string;
  category: string;
  status: string;
  /** Date de trouvaille (début). */
  dateFrom: string;
  /** Date de trouvaille (fin). */
  dateTo: string;
  /** Date de création de l’annonce (début). */
  createdFrom: string;
  /** Date de création de l’annonce (fin). */
  createdTo: string;
  location: string;
  attributes: Record<string, string>;
  provider_event_id: string;
  storage_box_id: string;
  /** Lieu prestataire (sous-espace / site). */
  provider_item_id: string;
}

/** Nombre de critères actifs (pour badge, etc.). */
export function countActiveFoundObjectFilters(
  filters: FilterState,
  lockedStatus?: string
): number {
  const hideStatusFilter = lockedStatus !== undefined && lockedStatus !== '';
  let count = 0;
  if (filters.search) count++;
  if (filters.referenceCode) count++;
  if (filters.category) count++;
  if (!hideStatusFilter && filters.status) count++;
  if (filters.dateFrom) count++;
  if (filters.dateTo) count++;
  if (filters.createdFrom) count++;
  if (filters.createdTo) count++;
  if (filters.location) count++;
  if (filters.provider_event_id) count++;
  if (filters.storage_box_id) count++;
  if (filters.provider_item_id) count++;
  count += Object.keys(filters.attributes).length;
  return count;
}

interface FoundObjectsFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onClearFilters: () => void;
  lockedStatus?: string;
  /** Sans carte MUI : à placer dans une modale ou un panneau. */
  embedded?: boolean;
}

const FoundObjectsFilters: React.FC<FoundObjectsFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  lockedStatus,
  embedded = false,
}) => {
  /** Statut imposé par le menu : pas de filtre statut affiché (ni désactivé). */
  const hideStatusFilter = lockedStatus !== undefined && lockedStatus !== '';

  // Récupérer les catégories pour le filtre
  const { data: categoriesData = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => response.data?.categories || [],
  });

  const { data: providerInfo } = useProviderInfo();

  const { data: providerItemsList = [] } = useQuery({
    queryKey: ['provider-items', providerInfo?.id],
    queryFn: async () => {
      const res = await apiService.getProviderItems(providerInfo!.id);
      return res.data.provider_items || [];
    },
    enabled: !!providerInfo?.id,
  });

  const { data: storageBoxesList = [] } = useQuery({
    queryKey: ['provider-storage-boxes', providerInfo?.id],
    queryFn: async () => {
      const res = await apiService.getStorageBoxesByProvider(providerInfo!.id);
      return res.data.storage_boxes || [];
    },
    enabled: !!providerInfo?.id,
  });

  const { data: providerEventsData } = useQuery({
    queryKey: ['provider-events'],
    queryFn: () => apiService.getProviderEvents(),
    enabled: !!providerInfo?.provider_type?.annual_event,
  });
  const providerEventsList = providerEventsData?.data?.provider_events ?? [];

  // Récupérer le template du partenaire pour les champs de lieu
  const { data: providerTemplateData } = useQuery({
    queryKey: ['providerTemplate', providerInfo?.type],
    queryFn: () => apiService.getProviderTemplate(providerInfo?.type || ''),
    enabled: !!providerInfo?.type,
  });

  const providerTemplate = providerTemplateData?.data || null;

  const categories = (categoriesData || []) as Category[];
  const selectedCategory = categories.find((cat) => cat.name === filters.category);

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    if (key === 'category') {
      onFiltersChange({
        ...filters,
        category: value,
        attributes: {},
      });
      return;
    }
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handleAttributeChange = (attributeKey: string, value: string) => {
    const newAttributes = { ...filters.attributes };
    if (value) {
      newAttributes[attributeKey] = value;
    } else {
      delete newAttributes[attributeKey];
    }
    const selectedCategory = categories.find((cat) => cat.name === filters.category);
    onFiltersChange({
      ...filters,
      attributes: pruneDependentAttributeFilters(newAttributes, selectedCategory),
    });
  };

  const activeCount = countActiveFoundObjectFilters(filters, lockedStatus);
  const hasActiveFilters = () => activeCount > 0;

  const cardContentSx = {
    px: { xs: 1.5, sm: 2 },
    py: { xs: 1.5, sm: 2 },
    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
  } as const;

  const inner = (
    <>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 1.5, sm: 0 }}
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <FilterListIcon />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Filtres
            </Typography>
            {hasActiveFilters() && (
              <Chip
                label={activeCount}
                color="primary"
                size="small"
              />
            )}
          </Box>
          <Box display="flex" gap={1} width={{ xs: '100%', sm: 'auto' }}>
            {hasActiveFilters() && (
              <Button
                startIcon={<ClearIcon />}
                onClick={onClearFilters}
                size="small"
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Effacer
              </Button>
            )}
          </Box>
        </Box>

        {/* Filtres de base toujours visibles */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={hideStatusFilter ? 6 : 4}>
            <TextField
              fullWidth
              label="Recherche globale"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              size="small"
              placeholder="Titre, destinataire, n° tracking…"
            />
          </Grid>
          <Grid item xs={12} md={hideStatusFilter ? 6 : 4}>
            <ReferenceSuffixFilterField
              value={filters.referenceCode}
              onChange={(suffix) => handleFilterChange('referenceCode', suffix)}
              providerId={providerInfo?.id}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={hideStatusFilter ? 6 : 4}>
            <FormControl fullWidth size="small">
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Catégorie"
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>Toutes les catégories</em>;
                  }
                  const cat = categories.find((c) => c.name === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon
                        icon={cat?.icon}
                        customIconUrl={cat?.custom_icon_url}
                        customIconFilename={cat?.custom_icon_filename}
                        size="small"
                        color="primary.main"
                      />
                      {selected}
                    </Box>
                  );
                }}
              >
                <MenuItem value="">Toutes les catégories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CategoryIcon
                        icon={category.icon}
                        customIconUrl={category.custom_icon_url}
                        customIconFilename={category.custom_icon_filename}
                        size="small"
                        color="primary.main"
                      />
                      {category.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {!hideStatusFilter && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Statut"
                >
                  <MenuItem value="">Tous les statuts</MenuItem>
                  <MenuItem value="found">Trouvé</MenuItem>
                  <MenuItem value="to_ship">À expédier</MenuItem>
                  <MenuItem value="in_transit">Parti en livraison</MenuItem>
                  <MenuItem value="claimed">Récupéré</MenuItem>
                  <MenuItem value="disposed">Éliminé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {providerInfo?.provider_type?.annual_event && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Événement / édition</InputLabel>
                <Select
                  value={filters.provider_event_id}
                  onChange={(e) => handleFilterChange('provider_event_id', e.target.value)}
                  label="Événement / édition"
                >
                  <MenuItem value="">Tous les événements</MenuItem>
                  {providerEventsList.map((ev) => (
                    <MenuItem key={ev.id} value={String(ev.id)}>
                      {ev.name} ({ev.year})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {/* Filtres avancés */}
        <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quand ? (début)"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quand ? (fin)"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lieu de trouvaille"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  size="small"
                  placeholder="Chambre, zone, etc."
                />
              </Grid>
            </Grid>

            {filters.category ? (
              <CategoryAttributeFilterControls
                category={categories.find((cat) => cat.name === filters.category)}
                attributeFilters={filters.attributes}
                onAttributeFiltersChange={(attributes) => onFiltersChange({ ...filters, attributes })}
              />
            ) : null}

            {/* Filtres de lieux de trouvaille dynamiques */}
            {(providerTemplate || providerItemsList.length > 0 || storageBoxesList.length > 0) && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Lieux de trouvaille ({providerInfo?.type})
                </Typography>
                <Grid container spacing={2}>
                  {storageBoxesList.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Boîte de stockage</InputLabel>
                        <Select
                          value={filters.storage_box_id || ''}
                          onChange={(e) => handleFilterChange('storage_box_id', e.target.value as string)}
                          label="Boîte de stockage"
                        >
                          <MenuItem value="">Toutes</MenuItem>
                          {(storageBoxesList as ProviderStorageBoxRow[])
                            .filter((b) => b.is_active !== false)
                            .map((b) => {
                              const pl = b.provider_place?.display_name || b.provider_place?.name || 'Lieu';
                              return (
                                <MenuItem key={b.id} value={String(b.id)}>
                                  {pl} — {b.name}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {storageBoxesList.length === 0 && providerItemsList.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Lieu (sous-espace)</InputLabel>
                        <Select
                          value={filters.attributes.provider_item_id || ''}
                          onChange={(e) =>
                            handleAttributeChange('provider_item_id', e.target.value as string)
                          }
                          label="Lieu (sous-espace)"
                        >
                          <MenuItem value="">Tous</MenuItem>
                          {providerItemsList
                            .filter((i: { is_active?: boolean }) => i.is_active !== false)
                            .slice()
                            .sort(comparePlacesByLabel)
                            .map((item: { id: number; display_name?: string; name: string }) => (
                              <MenuItem key={item.id} value={String(item.id)}>
                                {item.display_name || item.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {Object.entries(providerTemplate?.fields || {}).map(([fieldName, fieldConfig]: [string, any]) => {
                    if (fieldConfig.type === 'array' || fieldConfig.type === 'object') {
                      return null; // Ignorer les types complexes (dont zones obsolètes)
                    }

                    const fieldValue = filters.attributes[fieldName] || '';

                    if (fieldConfig.type === 'select' && fieldConfig.options) {
                      const options = Array.isArray(fieldConfig.options) 
                        ? fieldConfig.options 
                        : Object.entries(fieldConfig.options).map(([key, label]) => ({ value: key, label }));

                      return (
                        <Grid item xs={12} md={4} key={`location-${fieldName}`}>
                          <FormControl fullWidth size="small">
                            <InputLabel>{fieldConfig.label}</InputLabel>
                            <Select
                              value={fieldValue}
                              onChange={(e) => handleAttributeChange(fieldName, e.target.value)}
                              label={fieldConfig.label}
                            >
                              <MenuItem value="">Tous</MenuItem>
                              {options.map((option: any) => (
                                <MenuItem key={option.value || option} value={option.value || option}>
                                  {option.label || option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    }

                    if (fieldConfig.type === 'string' || fieldConfig.type === 'text') {
                      return (
                        <Grid item xs={12} md={4} key={`location-${fieldName}`}>
                          <TextField
                            fullWidth
                            label={fieldConfig.label}
                            value={fieldValue}
                            onChange={(e) => handleAttributeChange(fieldName, e.target.value)}
                            size="small"
                            placeholder={`Filtrer par ${fieldConfig.label.toLowerCase()}`}
                          />
                        </Grid>
                      );
                    }

                    return null;
                  })}
                </Grid>
              </Box>
            )}
          </Box>

        {/* Chips des filtres actifs */}
        {hasActiveFilters() && (
          <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
            {filters.search && (
              <Chip
                label={`Recherche: "${filters.search}"`}
                onDelete={() => handleFilterChange('search', '')}
                size="small"
              />
            )}
            {filters.referenceCode && (
              <Chip
                label={`Réf.: ${formatReferenceFilterLabel(providerInfo?.id, filters.referenceCode)}`}
                onDelete={() => handleFilterChange('referenceCode', '')}
                size="small"
              />
            )}
            {filters.category && (
              <Chip
                icon={
                  <Box sx={{ pl: 0.5, display: 'flex', alignItems: 'center' }}>
                    <CategoryIcon
                      icon={categories.find((c) => c.name === filters.category)?.icon}
                      customIconUrl={categories.find((c) => c.name === filters.category)?.custom_icon_url}
                      customIconFilename={
                        categories.find((c) => c.name === filters.category)?.custom_icon_filename
                      }
                      size="small"
                      color="inherit"
                    />
                  </Box>
                }
                label={`Catégorie: ${filters.category}`}
                onDelete={() => handleFilterChange('category', '')}
                size="small"
              />
            )}
            {!hideStatusFilter && filters.status && (
              <Chip
                label={`Statut: ${filters.status}`}
                onDelete={() => handleFilterChange('status', '')}
                size="small"
              />
            )}
            {filters.dateFrom && (
              <Chip
                label={`Depuis: ${new Date(filters.dateFrom).toLocaleDateString('fr-FR')}`}
                onDelete={() => handleFilterChange('dateFrom', '')}
                size="small"
              />
            )}
            {filters.dateTo && (
              <Chip
                label={`Jusqu'à: ${new Date(filters.dateTo).toLocaleDateString('fr-FR')}`}
                onDelete={() => handleFilterChange('dateTo', '')}
                size="small"
              />
            )}
            {filters.location && (
              <Chip
                label={`Lieu: ${filters.location}`}
                onDelete={() => handleFilterChange('location', '')}
                size="small"
              />
            )}
            {Object.entries(filters.attributes).map(([key, value]) => (
              <Chip
                key={key}
                label={`${formatAttributeLabel(key, {
                  categoryAttributes: selectedCategory?.attributes,
                  objectAttributes: filters.attributes,
                })}: ${value}`}
                onDelete={() => handleAttributeChange(key, '')}
                size="small"
              />
            ))}
          </Box>
        )}
    </>
  );

  if (embedded) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', ...cardContentSx }}>
        {inner}
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 3, maxWidth: '100%' }}>
      <CardContent sx={cardContentSx}>{inner}</CardContent>
    </Card>
  );
};

export default FoundObjectsFilters;
